import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Textarea,
  Stack,
  Avatar,
  Select,
  Option,
  Input,
  Alert,
  CircularProgress,
} from '@mui/joy';
import { ResponsiveTable, type ResponsiveColumn, type SortState } from '../../common/ResponsiveTable';
import {
  ArrowsClockwise as SyncIcon,
  ArrowBendUpLeft as ReplyIcon,
  Star as StarIcon,
  MagnifyingGlass as SearchIcon,
  Sparkle as AutoAwesomeIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { format, differenceInDays } from 'date-fns';

import type { Review, ReviewPage } from '../../../types/reviews';

interface ReviewPlatform {
  id: number;
  platform: string;
  account_name: string;
  is_active: boolean;
  google_business_profile?: number;
  place_id?: string;
}

export const MyReviews: React.FC = () => {
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [replyNotice, setReplyNotice] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inboxFilter, setInboxFilter] = useState(() => new URLSearchParams(window.location.search).get('inbox') || 'all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | null>({ key: 'date', direction: 'desc' });
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const queryClient = useQueryClient();

  // Fetch reviews
  const { data: reviewPage, isLoading, error: reviewsError, refetch } = useQuery({
    queryKey: ['reviews', searchQuery, platformFilter, ratingFilter, inboxFilter, page, sort],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), inbox: inboxFilter });
      params.set('ordering', sort ? `${sort.direction === 'desc' ? '-' : ''}${sort.key === 'date' ? 'review_date' : 'rating'}` : '-review_date');
      if (searchQuery) params.append('search', searchQuery);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      if (ratingFilter !== 'all') params.append('rating', ratingFilter);

      const response = await api.get<ReviewPage<Review>>(`/api/marketing/reviews/?${params}`);
      return response.data;
    },
  });

  const reviews = reviewPage?.results || [];
  React.useEffect(() => {
    if (reviewPage && page > Math.max(1, Math.ceil(reviewPage.count / 20))) setPage(Math.max(1, Math.ceil(reviewPage.count / 20)));
  }, [reviewPage, page]);

  const { data: assignees = [] } = useQuery({
    queryKey: ['review-assignees'],
    queryFn: async () => (await api.get<{ id: number; name: string }[]>('/api/marketing/reviews/assignees/')).data,
  });
  const assignReview = useMutation({
    mutationFn: async ({ id, owner }: { id: number; owner: number | null }) => api.patch(`/api/marketing/reviews/${id}/`, { assigned_to: owner }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['reviews'] }); },
  });

  // Fetch platforms for filter
  const { data: platforms = [], isPending: platformsLoading, error: platformsError, refetch: refetchPlatforms } = useQuery({
    queryKey: ['review-platforms'],
    queryFn: async () => {
      const response = await api.get<{ results: ReviewPlatform[] }>('/api/marketing/review-platforms/');
      return response.data.results || [];
    },
  });

  const connectedProfiles = platforms.filter(p => p.platform === 'google' && p.google_business_profile && p.is_active);
  const hasGBPPlatform = connectedProfiles.length > 0;

  // Sync reviews mutation
  const syncReviews = useMutation({
    mutationFn: async () => {
      try {
        const response = await api.post('/api/marketing/reviews/sync/', {});
        return response.data;
      } catch (error) {
        const details = error as { responseData?: { error?: string } };
        throw new Error(details.responseData?.error || 'Unable to sync reviews. Please try again.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-platforms'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
    },
    onError: (error: any) => {
      console.error('Sync failed:', error);
    },
  });

  // Respond to review mutation
  const respondToReview = useMutation({
    mutationFn: async ({ reviewId, response_text, publish = true }: { reviewId: number; response_text: string; publish?: boolean }) => {
      const response = await api.post<Review>(`/api/marketing/reviews/${reviewId}/respond/`, {
        response_text, publish,
      });
      return response.data;
    },
    onSuccess: (data: Review) => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      if (data.response_status === 'draft') { setReplyNotice('Draft saved.'); return; }
      setResponseModalOpen(false);
      setSelectedReview(null);
      setResponseText('');
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setReplyNotice(error.response?.data?.error || 'Unable to publish. Your text is still available to retry.');
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
    },
  });

  const handleGenerateWithAI = async () => {
    if (!selectedReview) return;
    setGeneratingAI(true);
    try {
      const start = await api.post(
        `/api/marketing/reviews/${selectedReview.id}/generate-response/`,
        {}
      );
      const data = start.data as {
        response_text?: string;
        task_id?: string;
        status?: string;
      };
      // Sync fallback (tests / older API) returns text immediately.
      if (data.response_text) {
        setResponseText(data.response_text);
        return;
      }
      const taskId = data.task_id;
      if (!taskId) {
        throw new Error('No task_id returned from generate-response');
      }
      // Poll Celery result — AI runs off the web dyno so Heroku p95 stays low.
      const deadline = Date.now() + 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 500));
        const statusResp = await api.get(
          `/api/marketing/reviews/${selectedReview.id}/generate-response-status/?task_id=${encodeURIComponent(taskId)}`
        );
        const status = statusResp.data as {
          status?: string;
          response_text?: string;
          error?: string;
        };
        if (status.status === 'completed') {
          setResponseText(status.response_text || '');
          return;
        }
        if (status.status === 'failed') {
          throw new Error(status.error || 'AI generation failed');
        }
      }
      throw new Error('AI generation timed out');
    } catch (err: unknown) {
      console.error('AI generation error:', err);
      setReplyNotice('AI draft generation failed. Try again or write your reply.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <StarIcon
        key={i}
        size={16}
        color={i < rating ? 'var(--joy-palette-warning-500)' : 'var(--joy-palette-neutral-300)'}
      />
    ));
  };

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, any> = {
      Google: 'primary',
      Facebook: 'info',
      Yelp: 'danger',
      TripAdvisor: 'success',
    };
    return colors[platform] || 'neutral';
  };

  const handleResponseSubmit = () => {
    if (selectedReview && responseText.trim()) {
      respondToReview.mutate({
        reviewId: selectedReview.id,
        response_text: responseText,
      });
    }
  };

  const reviewColumns: ResponsiveColumn<Review>[] = [
    {
      key: 'date',
      sortValue: (row) => row.review_date ? new Date(row.review_date) : null,
      header: 'Date',
      width: '10%',
      render: (r) => (
        <Typography level="body-sm">
          {format(new Date(r.review_date), 'MMM d, yyyy')}
        </Typography>
      ),
    },
    {
      key: 'reviewer',
      header: 'Reviewer',
      width: '13%',
      mobileVariant: 'header',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar size="sm" src={r.reviewer_avatar}>
            {r.reviewer_name[0]}
          </Avatar>
          <Typography level="title-sm">{r.reviewer_name}</Typography>
        </Box>
      ),
    },
    {
      key: 'rating',
      sortValue: (row) => row.rating,
      header: 'Rating',
      width: '10%',
      mobileVariant: 'header',
      render: (r) => <Box sx={{ display: 'flex' }}>{renderStars(r.rating)}</Box>,
    },
    {
      key: 'review',
      header: 'Review',
      width: '18%',
      mobileVariant: 'body',
      render: (r) => (
        <Typography
          level="body-sm"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: { xs: 4, md: 2 },
            WebkitBoxOrient: 'vertical',
          }}
        >
          {r.review_text}
        </Typography>
      ),
    },
    {
      key: 'platform',
      header: 'Platform',
      width: '8%',
      render: (r) => (
        <Chip size="sm" color={getPlatformColor(r.platform_name)}>
          {r.platform_name}
        </Chip>
      ),
    },
    {
      key: 'owner', header: 'Owner', width: '14%',
      render: (review) => <Select size="sm" sx={{ minWidth: 0, width: '100%' }} aria-label={`Owner for ${review.reviewer_name}`} value={review.assigned_to ?? 0}
        disabled={assignReview.isPending} onChange={(_, value) => assignReview.mutate({ id: review.id, owner: value || null })}>
        <Option value={0}>Unassigned</Option>
        {assignees.map(owner => <Option key={owner.id} value={owner.id}>{owner.name}</Option>)}
      </Select>,
    },
    {
      key: 'age', header: 'Waiting', width: '7%',
      render: (review) => <Typography level="body-sm">{review.response_published_at ? 'Replied' : `${Math.max(0, differenceInDays(new Date(), new Date(review.review_date)))} days`}</Typography>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '13%',
      render: (r) => (
        <Chip size="sm" color={r.response_status === 'published' ? 'success' : r.response_status === 'failed' ? 'danger' : 'neutral'}>
          {{ unanswered: 'Unanswered', unverified: 'Saved, unverified', draft: 'Draft', publishing: 'Publishing', published: 'Published', failed: 'Publication failed' }[r.response_status] || 'Unanswered'}
        </Chip>
      ),
    },
  ];

  return (
    <Box>
      {platformsError ? (
        <Alert color="danger" sx={{ mb: 3 }} endDecorator={
          <Button size="sm" variant="outlined" onClick={() => refetchPlatforms()}>Try again</Button>
        }>
          Unable to load your connected profiles. Please try again.
        </Alert>
      ) : !platformsLoading && !hasGBPPlatform ? (
        <Alert color="warning" variant="soft" sx={{ mb: 3, flexWrap: 'wrap' }} endDecorator={
          <Button component="a" href="/marketing/local-seo" size="sm">Connect Google Business Profile</Button>
        }>
          Connect your Google Business Profile in Local SEO to sync reviews. Your connection is shared across marketing pages.
        </Alert>
      ) : hasGBPPlatform ? (
        <Typography level="body-sm" sx={{ mb: 3 }}>
          Connected through Local SEO: {connectedProfiles.map(profile => profile.account_name).join(', ')}
        </Typography>
      ) : null}
      {syncReviews.isError && <Alert color="danger" sx={{ mb: 2 }}>
        {syncReviews.error instanceof Error ? syncReviews.error.message : 'Unable to sync reviews. Please try again.'}
      </Alert>}

      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }} aria-label="Review inbox views">
        {Object.entries({ all: 'All reviews', unanswered: 'Unanswered', attention: 'Needs attention', failed: 'Publication failed', mine: 'Assigned to me' }).map(([value, label]) =>
          <Button key={value} size="sm" variant={inboxFilter === value ? 'solid' : 'outlined'} aria-pressed={inboxFilter === value}
            onClick={() => { setInboxFilter(value); setPage(1); }}>{label}</Button>)}
      </Stack>
      {assignReview.isError && <Alert color="danger">Unable to change the owner. Please try again.</Alert>}
      {/* Actions Bar */}
<Stack direction="row" gap={1} sx={{ mb: 2 }}>        <Button
          variant="outlined"
          color="primary"
          startDecorator={<SyncIcon />}
          onClick={() => syncReviews.mutate()}
          loading={syncReviews.isPending}
          disabled={platformsLoading || !!platformsError || !hasGBPPlatform}
        >
          Sync Reviews
        </Button>

        {syncReviews.isSuccess && (
          <Chip color="success" variant="soft">
            Reviews synced successfully
          </Chip>
        )}



</Stack>
      <CollapsibleFilters><Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Filters */}
        <Input
          placeholder="Search reviews..."
          startDecorator={<SearchIcon />}
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          sx={{ minWidth: 200 }}
        />

        <Select
          value={platformFilter}
          onChange={(_, value) => { setPlatformFilter(value as string); setPage(1); }}
          sx={{ minWidth: 150 }}
        >
          <Option value="all">All Platforms</Option>
          {platforms.map((platform) => (
            <Option key={platform.id} value={platform.id.toString()}>
              {platform.account_name}
            </Option>
          ))}
        </Select>

        <Select
          value={ratingFilter}
          onChange={(_, value) => { setRatingFilter(value as string); setPage(1); }}
          sx={{ minWidth: 120 }}
        >
          <Option value="all">All Ratings</Option>
          <Option value="5">5 Stars</Option>
          <Option value="4">4 Stars</Option>
          <Option value="3">3 Stars</Option>
          <Option value="2">2 Stars</Option>
          <Option value="1">1 Star</Option>
        </Select>
      </Box></CollapsibleFilters>

      {reviewsError && <Alert color="danger">Unable to load reviews. Please try again.</Alert>}
      {/* Reviews Table */}
      <ResponsiveTable<Review>
        rows={reviews}
        pagination={{ page, pageSize: 20, totalCount: reviewPage?.count || 0, onPageChange: setPage, itemNoun: 'reviews' }}
        getRowKey={(r) => r.id}
        columns={reviewColumns}
        sort={sort}
        onSortChange={setSort}
        actionsWidth="7%"
        rowActions={(review) => (
          <IconButton
            size="sm"
            aria-label={`Reply to ${review.reviewer_name}`}
            variant="plain"
            onClick={() => {
              setSelectedReview(review);
              setResponseText(review.response_draft || review.response_text || '');
              setReplyNotice(review.response_error || '');
              setResponseModalOpen(true);
            }}
          >
            <ReplyIcon />
          </IconButton>
        )}
        emptyState={
          !isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                No reviews found. Click "Sync Reviews" to fetch from connected platforms.
              </Typography>
            </Box>
          ) : null
        }
      />

      {/* Response Modal */}
      <Modal open={responseModalOpen} onClose={() => setResponseModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 600 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Respond to Review
          </Typography>

          {selectedReview && (
            <Stack spacing={2}>
              {/* Original Review */}
              <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Avatar src={selectedReview.reviewer_avatar}>
                    {selectedReview.reviewer_name[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-sm">{selectedReview.reviewer_name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {renderStars(selectedReview.rating)}
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {format(new Date(selectedReview.review_date), 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography level="body-sm">{selectedReview.review_text}</Typography>
              </Box>

              {replyNotice && <Alert color={replyNotice === 'Draft saved.' ? 'success' : 'warning'}>{replyNotice}</Alert>}
              {/* Response Form */}
              <FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <FormLabel sx={{ mb: 0 }}>Your Response</FormLabel>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={generatingAI ? <CircularProgress size="sm" /> : <AutoAwesomeIcon />}
                    onClick={handleGenerateWithAI}
                    disabled={generatingAI}
                  >
                    Write with AI
                  </Button>
                </Box>
                <Textarea
                  placeholder="Thank you for your feedback..."
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  minRows={4}
                  maxRows={8}
                />
              </FormControl>

              {selectedReview.response_published_at && (
                <Alert color="primary" variant="soft">
                  Confirmed published on {format(new Date(selectedReview.response_published_at), 'MMM d, yyyy')}
                  {selectedReview.responded_by_name && ` by ${selectedReview.responded_by_name}`}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="plain" onClick={() => setResponseModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="outlined" disabled={!responseText.trim() || respondToReview.isPending}
                  onClick={() => selectedReview && respondToReview.mutate({ reviewId: selectedReview.id, response_text: responseText, publish: false })}>
                  Save draft
                </Button>
                <Button
                  onClick={handleResponseSubmit}
                  loading={respondToReview.isPending}
                  disabled={!responseText.trim()}
                >
                  {selectedReview.response_status === 'failed' ? 'Retry publication' : 'Publish reply'}
                </Button>
              </Box>
            </Stack>
          )}
        </ModalDialog>
      </Modal>

    </Box>
  );
};
